import React, { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AuthLayout from '../components/AuthLayout';
import TextInput from '../components/TextInput';
import Select from '../components/Select';
import PasswordInput from '../components/PasswordInput';
import PasswordRequirements from '../components/PasswordRequirements';
import Button from '../components/Button';
import ErrorBanner from '../components/ErrorBanner';
import { register } from '../api/authApi';
import { getObrasSociales, ObraSocialOption } from '../api/obraSocialApi';
import { ApiError } from '../types/auth.types';
import loginStyles from './LoginPage.module.css';
import styles from './RegisterPage.module.css';

type FieldErrors = {
  nombre?: string;
  apellido?: string;
  dni?: string;
  email?: string;
  obraSocial?: string;
  nroAfiliado?: string;
  password?: string;
  confirmPassword?: string;
};

export default function RegisterPage() {
  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [dni, setDni] = useState('');
  const [genero, setGenero] = useState<'M' | 'H'>('M');
  const [email, setEmail] = useState('');
  const [particular, setParticular] = useState(false);
  const [obraSocial, setObraSocial] = useState('');
  const [nroAfiliado, setNroAfiliado] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [obrasSociales, setObrasSociales] = useState<ObraSocialOption[]>([]);
  const [loadingObrasSociales, setLoadingObrasSociales] = useState(true);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    getObrasSociales()
      .then(setObrasSociales)
      .finally(() => setLoadingObrasSociales(false));
  }, []);

  function handleParticular(checked: boolean) {
    setParticular(checked);
    if (checked) {
      setObraSocial('');
      setNroAfiliado('');
      setFieldErrors((prev) => ({ ...prev, obraSocial: undefined, nroAfiliado: undefined }));
    }
  }

  function validate(): boolean {
    const errors: FieldErrors = {};
    if (!nombre.trim()) errors.nombre = 'Ingresá el nombre.';
    if (!apellido.trim()) errors.apellido = 'Ingresá el apellido.';
    if (!dni.trim()) {
      errors.dni = 'Ingresá el DNI.';
    } else if (!/^\d{7,8}$/.test(dni.trim())) {
      errors.dni = 'El DNI debe tener 7 u 8 dígitos.';
    }
    if (!email.trim()) errors.email = 'Ingresá tu email.';
    if (!particular) {
      if (!obraSocial.trim()) errors.obraSocial = 'Seleccioná la obra social.';
      if (!nroAfiliado.trim()) errors.nroAfiliado = 'Ingresá el número de afiliado.';
    }
    if (!password) errors.password = 'Ingresá una contraseña.';
    if (password !== confirmPassword) errors.confirmPassword = 'Las contraseñas no coinciden.';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setGeneralError(null);

    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      await register({
        nombre: nombre.trim(),
        apellido: apellido.trim(),
        dni: dni.trim(),
        genero,
        email: email.trim(),
        particular,
        obraSocial: particular ? '' : obraSocial.trim(),
        nroAfiliado: particular ? '' : nroAfiliado.trim(),
        password,
        confirmPassword,
      });
      setSuccess(true);
    } catch (error) {
      if (axios.isAxiosError<ApiError>(error)) {
        const codigo = error.response?.data?.codigo;
        const mensaje = error.response?.data?.mensaje ?? 'Ocurrió un error al crear la cuenta.';
        if (codigo === 'DNI_DUPLICADO') {
          setFieldErrors((prev) => ({ ...prev, dni: mensaje }));
        } else if (codigo === 'EMAIL_DUPLICADO') {
          setFieldErrors((prev) => ({ ...prev, email: mensaje }));
        } else {
          setGeneralError(mensaje);
        }
      } else {
        setGeneralError('Ocurrió un error al crear la cuenta.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      title="Crear cuenta"
      subtitle="Laboratorio Sagrado Corazón"
      maxWidth={640}
      topPadding={5}
      logoMarginBottom={1}
      logoSize={150}
      titleColor="#1d4e6b"
      footer={<span>© 2026 SC Lab. Todos los derechos reservados.</span>}
    >
      {success ? (
        <div className={styles.success}>
          <p>Tu cuenta fue creada. Revisá tu email para confirmarla antes de iniciar sesión.</p>
          <Link to="/login" className={loginStyles.forgotLink} style={{ display: 'inline-block', marginTop: 8 }}>
            Ir a iniciar sesión
          </Link>
        </div>
      ) : (
      <form onSubmit={handleSubmit} noValidate>
        {generalError && <ErrorBanner message={generalError} onClose={() => setGeneralError(null)} />}

        <div className={styles.row3}>
          <TextInput id="nombre" label="Nombre" value={nombre} onChange={setNombre} error={fieldErrors.nombre} />
          <TextInput
            id="apellido"
            label="Apellido"
            value={apellido}
            onChange={setApellido}
            error={fieldErrors.apellido}
          />
          <TextInput id="dni" label="DNI" value={dni} onChange={setDni} error={fieldErrors.dni} />
        </div>

        <div className={styles.row3}>
          <Select
            id="genero"
            label="Género"
            value={genero}
            onChange={(value) => setGenero(value as 'M' | 'H')}
            options={[
              { value: 'M', label: 'Femenino' },
              { value: 'H', label: 'Masculino' },
            ]}
          />
          <TextInput
            id="email"
            label="Email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
            error={fieldErrors.email}
          />
          <div className={styles.checkboxField}>
            <input
              id="particular"
              type="checkbox"
              checked={particular}
              onChange={(e) => handleParticular(e.target.checked)}
            />
            <label htmlFor="particular">Paciente particular</label>
          </div>
        </div>

        {!particular && (
          <>
            <hr className={styles.divider} />
            <div className={styles.row2}>
              <Select
                id="obraSocial"
                label="Obra social"
                value={obraSocial}
                onChange={setObraSocial}
                options={obrasSociales.map((o) => ({ value: o.nombre, label: o.nombre }))}
                placeholder={loadingObrasSociales ? 'Cargando...' : 'Seleccioná una obra social'}
                disabled={loadingObrasSociales}
                error={fieldErrors.obraSocial}
              />
              <TextInput
                id="nroAfiliado"
                label="N° de afiliado"
                value={nroAfiliado}
                onChange={setNroAfiliado}
                error={fieldErrors.nroAfiliado}
              />
            </div>
          </>
        )}

        <hr className={styles.divider} />

        <div className={styles.passwordRow}>
          <div className={styles.row2}>
            <PasswordInput
              id="password"
              label="Contraseña"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              error={fieldErrors.password}
            />
            <PasswordInput
              id="confirmPassword"
              label="Confirmar contraseña"
              value={confirmPassword}
              onChange={setConfirmPassword}
              autoComplete="new-password"
              error={fieldErrors.confirmPassword}
            />
          </div>

          <div className={styles.passwordHint}>
            <div className={styles.passwordHintHeader}>
              <span className={styles.passwordHintIcon}>!</span>
              <strong>Requisitos de la contraseña</strong>
            </div>
            <PasswordRequirements password={password} />
          </div>
        </div>

        <Button type="submit" loading={loading} loadingText="Creando cuenta...">
          Crear cuenta
        </Button>
      </form>
      )}

      {!success && (
        <div className={loginStyles.forgotLinkWrapper} style={{ justifyContent: 'center', marginTop: 16 }}>
          <Link to="/login" className={loginStyles.forgotLink}>
            ¿Ya tenés cuenta? Iniciar sesión
          </Link>
        </div>
      )}
    </AuthLayout>
  );
}
